'use client';
/**
 * LA LISTE DES INDISPENSABLES, À IMPRIMER (RQ, 20/10/2026).
 *
 * « La liste peut être imprimée et affichée si besoin. » Une feuille A4
 * paysage : les réactifs et consommables indispensables, groupés par famille,
 * avec leur point de commande. Avec les colonnes de comptage, la même feuille
 * sert à l'inventaire mensuel au poste, avant la saisie dans Nafis.
 *
 * ── Ce que la feuille refuse ────────────────────────────────────────────────
 *  · AFFICHER LE STOCK ATTENDU à côté de la case « compté » : un compteur qui
 *    lit le chiffre attendu le recopie. On compte à l'aveugle ; l'écart se
 *    calcule à la saisie.
 *  · SE FAIRE PASSER POUR LA RÉFÉRENCE : la date d'édition est imprimée, et la
 *    mention dit que la liste en vigueur est celle de l'application.
 *
 * La feuille est rendue dans un portail sous `body` : l'impression masque
 * tout le reste, la fenêtre comprise — la règle `visibility` des autres
 * écrans laisse la page sous une fenêtre ouverte décaler la feuille.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { I, Modale } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import { enTeteCollant } from '@/shared/Tableau';
import { fmtDate } from '@/lib/meta';
import { moteur } from './manuel/Registres';

export type LigneStock = {
  code: string; libelle: string; famille: string; fabricant: string; fournisseurNom: string; refFournisseur: string;
  conditionnement: string; conservation: string; discipline: string;
  indispensable: boolean; stockMinimum: number | null; uniteStock: string; nbMouvements: number;
  quantite: number | null; depuisComptage: boolean; dernierComptage: string | null; prochainComptage: string | null;
  comptageEnRetard: boolean; seuilAtteint: boolean;
};

export const FAMILLES: Record<string, string> = {
  reactif: 'Réactifs', controle: 'Matériaux de contrôle', calibrant: 'Calibrants', consommable: 'Consommables',
};
const ORDRE_FAMILLE = ['reactif', 'consommable', 'controle', 'calibrant'];
export const nombre = (n: number | null | undefined) => (n === null || n === undefined ? '—' : String(n).replace('.', ','));

const CSS = `
.liste-indisp-impr { display: none; }
@media print {
  @page { size: A4 landscape; margin: 10mm; }
  body > *:not(.liste-indisp-impr) { display: none !important; }
  .liste-indisp-impr { display: block; }
}
.liste-indisp table { width: 100%; border-collapse: collapse; font-size: 11.5px; }
.liste-indisp th, .liste-indisp td { border: 1px solid var(--text-strong); padding: 5px 7px; text-align: start; vertical-align: top; color: var(--text-strong); }
.liste-indisp th { font-size: 10px; text-transform: uppercase; letter-spacing: .03em; background: var(--surface-sunken); }
.liste-indisp tr.famille td { font-weight: 700; background: var(--surface-sunken); }
.liste-indisp td.case { min-width: 70px; }
.liste-indisp tr { break-inside: avoid; }
`;

function Feuille({ lignes, comptage, edition }: { lignes: LigneStock[]; comptage: boolean; edition: string }) {
  const groupes = useMemo(() => ORDRE_FAMILLE
    .map((f) => ({ f, l: lignes.filter((x) => x.famille === f).sort((a, b) => a.libelle.localeCompare(b.libelle, 'fr')) }))
    .concat([{ f: 'autre', l: lignes.filter((x) => !ORDRE_FAMILLE.includes(x.famille)) }])
    .filter((g) => g.l.length), [lignes]);
  const nbCol = comptage ? 9 : 6;
  return (
    <div className="liste-indisp" style={{ display: 'grid', gap: 8, color: 'var(--text-strong)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 16 }}>Réactifs et consommables indispensables</strong>
        <span style={{ fontSize: 11.5 }}>Édité le {fmtDate(edition)} · {lignes.length} produit(s) · comptage au moins une fois par mois</span>
      </div>
      <table className="ged-tableau" ref={enTeteCollant}>
        <thead>
          <tr>
            <th scope="col">Produit</th><th scope="col">Fabricant · fournisseur</th><th scope="col">Conditionnement</th><th scope="col">Conservation</th>
            <th scope="col">Unité</th><th scope="col">Commander à</th>
            {comptage && <><th scope="col">Compté</th><th scope="col">Date</th><th scope="col">Visa</th></>}
          </tr>
        </thead>
        <tbody>
          {groupes.map((g) => (
            <React.Fragment key={g.f}>
              <tr className="famille"><td colSpan={nbCol}>{FAMILLES[g.f] || 'Autres'}</td></tr>
              {g.l.map((x) => (
                <tr key={x.code}>
                  <td><strong>{x.libelle}</strong>{x.refFournisseur && <div style={{ fontSize: 10.5 }}>Réf. {x.refFournisseur}</div>}</td>
                  <td>{[x.fabricant, x.fournisseurNom].filter(Boolean).join(' · ') || '—'}</td>
                  <td>{x.conditionnement || '—'}</td>
                  <td>{x.conservation || '—'}</td>
                  <td>{x.uniteStock || '—'}</td>
                  <td>{x.stockMinimum !== null ? nombre(x.stockMinimum) : '—'}</td>
                  {comptage && <><td className="case" /><td className="case" /><td className="case" /></>}
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      <div style={{ fontSize: 10.5 }}>
        Document tiré de Nafis — la liste en vigueur est celle de l’application (Matériel › Stock › Quantités).
        {comptage && ' Les quantités comptées se saisissent dans Nafis : l’écart au registre y est calculé.'}
      </div>
    </div>
  );
}

export function ListeIndispensables({ onClose, lignes: fournies }: { onClose: () => void; lignes?: LigneStock[] }) {
  const [lignes, setLignes] = useState<LigneStock[] | null>(fournies ?? null);
  const [edition, setEdition] = useState('');
  const [erreur, setErreur] = useState('');
  const [comptage, setComptage] = useState(true);
  useEffect(() => {
    let vivant = true;
    void moteur('stock-quantites').then((r) => {
      if (!vivant) return;
      if (!r.ok) { setErreur(r.message || 'La liste n’a pas pu être chargée.'); return; }
      setEdition(String(r.data.aujourdhui || ''));
      if (!fournies) setLignes((r.data.lignes as LigneStock[]) || []);
    });
    return () => { vivant = false; };
  }, [fournies]);
  const indisp = useMemo(() => (lignes || []).filter((l) => l.indispensable), [lignes]);

  return (
    <Modale titre="Liste des indispensables" largeur={980} onClose={onClose}
      sousTitre="Aperçu de la feuille A4 paysage — à afficher, ou à emporter pour l’inventaire mensuel.">
      <style>{CSS}</style>
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={comptage} onChange={(e) => setComptage(e.target.checked)} />
            Colonnes de comptage (compté, date, visa)
          </label>
          <span style={{ flex: 1 }} />
          <Bouton variante="primaire" taille="sm" disabled={!indisp.length || !edition} onClick={() => window.print()}>
            <I n="print" s={13} />Imprimer
          </Bouton>
        </div>
        {erreur && <div role="alert" style={{ color: 'var(--red-600)', fontSize: 12.5 }}>{erreur}</div>}
        {lignes === null && !erreur && <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Chargement…</div>}
        {lignes !== null && !indisp.length && (
          <div style={{ fontSize: 12.5 }}>Aucun produit n’est marqué indispensable. Cochez « Indispensable » dans la fiche des produits concernés, au catalogue.</div>
        )}
        {!!indisp.length && edition && (
          <div style={{ overflowX: 'auto', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: 12, background: 'var(--surface-card)' }}>
            <Feuille lignes={indisp} comptage={comptage} edition={edition} />
          </div>
        )}
      </div>
      {!!indisp.length && edition && typeof document !== 'undefined' && createPortal(
        <div className="liste-indisp-impr"><Feuille lignes={indisp} comptage={comptage} edition={edition} /></div>,
        document.body,
      )}
    </Modale>
  );
}
